/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  styleguideBorderWidth,
  styleguideElevation,
  styleguideFontFamily,
  styleguideLetterSpacing,
  styleguideRadius,
  styleguideSpacing,
} from '@kaiizen/kai-shiki/core';

const OVERLAY_STYLE = css`
  label: MODAL_BASE_OVERLAY_STYLE;
  position: fixed;
  inset: 0;
  /* Above every other modal in the app (SettingsModal/FeatureFlagsModal/SuperAdminToolsModal/CacheInfoModal/
     price-approval dialogs and others all sit at z-index 2000) - a confirm triggered from
     inside one of those must not render behind it. Matches the z-index the legacy
     confirm dialog this replaces already used. */
  z-index: 3000;
  display: flex;
  /* The card centers itself with margin: auto rather than align-items: center, so a card
     taller than the screen (a phone held sideways) scrolls instead of losing its top and
     bottom - and with them its only buttons - off-screen. */
  overflow-y: auto;
  /* Keeps the card off the screen edges on phones, where it would otherwise fill the width. */
  padding: ${styleguideSpacing.s4}px;
  background: rgba(26, 26, 26, 0.5);
`;

const CARD_STYLE = (maxWidth: number) =>
  css`
  label: MODAL_BASE_CARD_STYLE;
  background: ${styleguideColors.white};
  border: ${styleguideBorderWidth.thin}px solid ${styleguideColors.ink};
  box-shadow: ${styleguideElevation.modal};
  padding: ${styleguideSpacing.s6}px;
  max-width: ${maxWidth}px;
  width: 100%;
  margin: auto;
`;

const ICON_CIRCLE_STYLE = css`
  label: MODAL_BASE_ICON_CIRCLE_STYLE;
  width: 48px;
  height: 48px;
  border-radius: ${styleguideRadius.circle};
  background: ${styleguideColors.accent};
  color: ${styleguideColors.ink};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${styleguideSpacing.s4}px;
`;

const TITLE_STYLE = css`
  label: MODAL_BASE_TITLE_STYLE;
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(styleguideFontSize.displayModalTitle)};
  font-weight: ${styleguideFontWeight.bold};
  color: ${styleguideColors.ink};
  letter-spacing: -0.01em;
`;

// A function, not a static constant - the one property that varies per-instance
// (header alignment) is the only thing this branches on.
const HEADER_STYLE = (centered: boolean) =>
  css`
  label: MODAL_BASE_HEADER_STYLE;
  display: flex;
  flex-direction: column;
  align-items: ${centered ? 'center' : 'flex-start'};
  text-align: ${centered ? 'center' : 'left'};
`;

const BODY_STYLE = css`
  label: MODAL_BASE_BODY_STYLE;
  margin-top: ${styleguideSpacing.s2}px;
  margin-bottom: ${styleguideSpacing.s5}px;
  /* Unlike TITLE_STYLE/BUTTON_BASE_STYLE, nothing here set a font-family before —
     body content silently fell back to the browser's default serif font instead
     of the styleguide's body face. */
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(styleguideFontSize.bodyDefault)};
`;

const ACTIONS_STYLE = css`
  label: MODAL_BASE_ACTIONS_STYLE;
  display: flex;
  gap: ${styleguideSpacing.s2}px;
  justify-content: flex-end;
`;

const BUTTON_BASE_STYLE = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10.5)};
  font-weight: ${styleguideFontWeight.bold};
  letter-spacing: ${styleguideLetterSpacing.monoLabel};
  text-transform: uppercase;
  padding: 9px 16px;
  cursor: pointer;
  border: 1.5px solid transparent;
  white-space: nowrap;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

// Consumed via a raw <button css={...}>, not kai-shiki's CommonActionButton -
// CommonActionButton only accepts a `style` object (no :hover support) and
// Emotion's css prop is stripped before a component ever sees it, same
// gotcha mainNavigationStyles.ts already documents for the same reason.
export const MODAL_BASE_SECONDARY_BUTTON_STYLE = css`
  ${BUTTON_BASE_STYLE};
  label: MODAL_BASE_SECONDARY_BUTTON_STYLE;
  background: ${styleguideColors.white};
  color: ${styleguideColors.ink};
  border-color: ${styleguideColors.stroke};

  &:hover { border-color: ${styleguideColors.ink}; }
`;

export const MODAL_BASE_PRIMARY_BUTTON_STYLE = css`
  ${BUTTON_BASE_STYLE};
  label: MODAL_BASE_PRIMARY_BUTTON_STYLE;
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
  border-color: ${styleguideColors.ink};

  &:hover { background: #000; border-color: #000; }
`;

/**
 * A de-emphasized third path, styled as a text link rather than a button —
 * it's a less common way to resolve the dialog, not equally weighted against
 * Confirm/Cancel.
 */
export const MODAL_BASE_LINK_BUTTON_STYLE = css`
  ${BUTTON_BASE_STYLE};
  label: MODAL_BASE_LINK_BUTTON_STYLE;
  background: none;
  border-color: transparent;
  padding: 9px 4px;
  color: ${styleguideColors.ink};
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover { color: #000; }
`;

export const ModalBase = ({
  icon,
  title,
  centered = false,
  maxWidth = 480,
  children,
  actions,
  onOverlayClick,
}: {
  icon?: ReactNode;
  title: string;
  /** Dispatch's own header centers icon+title; the plain confirm-dialog case does not. */
  centered?: boolean;
  maxWidth?: number;
  children: ReactNode;
  actions: ReactNode;
  onOverlayClick?: () => void;
}) => (
  <div css={OVERLAY_STYLE} onClick={onOverlayClick}>
    <div css={CARD_STYLE(maxWidth)} onClick={event => event.stopPropagation()}>
      <div css={HEADER_STYLE(centered)}>
        {icon ? <div css={ICON_CIRCLE_STYLE}>{icon}</div> : null}
        <div css={TITLE_STYLE}>{title}</div>
      </div>
      <div css={BODY_STYLE}>{children}</div>
      <div css={ACTIONS_STYLE}>{actions}</div>
    </div>
  </div>
);
